import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
});

// Add auth token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 responses (token expired)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on login page or during login/register attempts
    const isAuthEndpoint = error.config.url === '/token' || error.config.url === '/users/';
    
    if (error.response && error.response.status === 401 && !isAuthEndpoint) {
      // Store the current page and state in localStorage before redirecting
      const currentPath = window.location.hash.substring(1) || window.location.pathname;
      if (currentPath !== '#/auth') {
        localStorage.setItem('auth_redirect', currentPath);
        localStorage.setItem('auth_message', 'Your session has expired. Please log in again to continue.');
      }
      
      // Remove token and redirect to auth page
      localStorage.removeItem('token');
      window.location.href = '#/auth';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const auth = {
  login: (email, password) => api.post('/token', { username: email, password }),
  register: (userData) => api.post('/register', userData),
  getCurrentUser: () => api.get('/users/me'),
  verifyEmail: (token) => api.post('/verify-email', { token }),
  resendVerification: (email) => api.post('/resend-verification', { email }),
  forgotPassword: (email) => api.post('/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/reset-password', { token, new_password }),
  getUserUsageStats: () => api.get('/users/me/usage-stats'),
  preRegisterPayment: (sessionId, planId) => api.post('/pre-register-payment', { session_id: sessionId, plan_id: planId }),
  completePaymentRegistration: (sessionId, planId, email) => api.post('/complete-payment-registration', { session_id: sessionId, plan_id: planId, email }),
};

// Feedback endpoint
export const feedback = {
  submitFeedback: (feedbackData) => api.post('/feedback', feedbackData),
};

// Menu analysis endpoints
export const menuAnalysis = {
  analyzeMenu: (formData) => api.post('/analyze-menu', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  getAnalysisHistory: () => api.get('/analysis-history'),
};

// Analysis service for details and operations
export const analysisService = {
  getAnalysisDetails: (analysisId) => api.get(`/analysis/${analysisId}`),
};

// User profile and preferences endpoints
export const userProfile = {
  updateProfile: (userData) => api.put('/users/me', userData),
  getDietaryPreferences: () => api.get('/users/me/dietary-preferences'),
  updateDietaryPreferences: (preferences) => api.put('/users/me/dietary-preferences', preferences),
  getDietaryRestrictions: () => api.get('/users/me/dietary-restrictions'),
  removeDietaryRestriction: (id) => api.delete(`/users/me/dietary-restrictions/${id}`),
  updateDietaryRestrictions: (restrictionIds) => api.put('/users/me/dietary-restrictions', { restriction_ids: restrictionIds }),
  getAnalysisHistory: () => api.get('/analysis-history'),
  getAvailableRestrictions: () => api.get('/users/available-dietary-restrictions'),
  updateNotificationSettings: (settings) => api.put('/users/me/notification-settings', settings),
  getUsageStats: () => api.get('/users/me/usage-stats'),
  deleteAccount: () => api.delete('/users/me'),
  switchPlan: (planId, checkoutSessionId) => api.post('/users/me/switch-plan', { plan_id: planId, checkout_session_id: checkoutSessionId }),
};

// Plans and usage endpoints
export const plans = {
  getPlans: () => api.get('/plans'),
  createCheckoutSession: (planId, isUpgrade = false, email = null) => 
    api.post('/create-checkout-session', { 
      plan_id: planId, 
      is_upgrade: isUpgrade,
      email: email 
    }),
  getStripePublicKey: () => api.get('/stripe-public-key'),
  verifyPayment: (sessionId) => api.get(`/verify-payment/${sessionId}`),
};

// Admin endpoints
export const admin = {
  getInvitations: () => api.get('/admin/invitations'),
  createInvitation: (invitationData) => api.post('/admin/invitations', invitationData),
  deleteInvitation: (id) => api.delete(`/admin/invitations/${id}`),
  getUsers: () => api.get('/admin/users'),
  toggleUserStatus: (userId) => api.put(`/admin/users/${userId}/toggle-status`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  // Plan management endpoints
  getPlans: () => api.get('/admin/plans'),
  createPlan: (planData) => api.post('/admin/plans', planData),
  updatePlan: (planId, planData) => api.put(`/admin/plans/${planId}`, planData),
  deletePlan: (planId) => api.delete(`/admin/plans/${planId}`),
  togglePlanStatus: (planId) => api.put(`/admin/plans/${planId}/toggle-status`),
  // Version information endpoint
  getVersion: () => api.get('/admin/version'),
};

// Plan invitation endpoints
export const invitations = {
  create: (invitationData) => api.post('/admin/invitations', invitationData),
  list: () => api.get('/admin/invitations'),
  revoke: (id) => api.delete(`/admin/invitations/${id}`),
  verify: (token) => api.get(`/invitation/${token}`),
  redeem: (token, userData) => api.post('/redeem-invitation', { token, ...userData }),
};

export default api;